import { useCallback, useEffect, useState } from 'react';

import { StatusCode } from '../../../api/enumerations';
import { getAllWorkOrders } from '../../../api/workOrders';
import {
  GetWorkOrdersParams,
  WorkOrderData,
} from '../../../api/workOrders/types';
import { flattenWorkOrders } from '../../../api/workOrders/utils';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useSnackbar from '../../../hooks/useSnackbar';
import { applyClientSideFilters } from './extendedFilters';

interface UseWorkOrdersFlatArgs {
  enabled: boolean;
  searchField: string;
  filters: GetWorkOrdersParams | undefined;
}

export interface UseWorkOrdersFlatReturn {
  data: WorkOrderData[];
  loading: boolean;
  refetch: () => void;
}

const buildParams = (
  searchField: string,
  filters: GetWorkOrdersParams | undefined
): GetWorkOrdersParams => {
  const params: GetWorkOrdersParams = {};
  if (searchField.length > 0) params.search = searchField;
  if (filters?.uf) params.uf = filters.uf;
  if (filters?.city) params.city = filters.city;
  if (filters?.licenseCompanyId)
    params.licenseCompanyId = filters.licenseCompanyId;
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  return params;
};

export function useWorkOrdersFlat({
  enabled,
  searchField,
  filters,
}: UseWorkOrdersFlatArgs): UseWorkOrdersFlatReturn {
  const [data, setData] = useState<WorkOrderData[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const refetch = useCallback(() => setRefreshToken((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setLoading(true);

    const params = buildParams(searchField, filters);

    getAllWorkOrders(params)
      .then((response) => {
        if (cancelled) return;
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        const flat = flattenWorkOrders(response.data);
        setData(applyClientSideFilters(flat, filters));
      })
      .catch((error) => {
        if (cancelled) return;
        handleSnackbar(getErrorMessage(error), true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, searchField, filters, refreshToken]);

  return { data, loading, refetch };
}
